import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, useEffect, useRef, useCallback } from 'react'
import { motion } from 'framer-motion'
import { Play, ArrowLeft } from 'lucide-react'
import { useIsTVDevice } from '../hooks/useTVNavigation'
import type { WatchHistory } from '../../../shared/types/watch-history.types'

// Utility functions from SourceSelectionDialog
const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}

const formatTimestamp = (currentSeconds: number, totalSeconds: number): string => {
  return `${formatDuration(currentSeconds)} / ${formatDuration(totalSeconds)}`
}

const getSourceBadgeColor = (sourceType: string): string => {
  switch (sourceType) {
    case 'vidfast':
      return 'bg-purple-600/90 text-white'
    case 'vidlink':
      return 'bg-pink-600/90 text-white'
    case 'gdrive':
      return 'bg-blue-600/90 text-white'
    case 'filestream':
      return 'bg-green-600/90 text-white'
    default:
      return 'bg-gray-600/90 text-white'
  }
}

const getSourceLabel = (sourceType: string): string => {
  switch (sourceType) {
    case 'vidfast':
      return 'VidFast'
    case 'vidlink':
      return 'VidLink'
    case 'gdrive':
      return 'Google Drive'
    case 'filestream':
      return 'FileStream'
    default:
      return sourceType
  }
}

const extractQualityFromFilename = (source: WatchHistory): string | null => {
  if (source.quality) {
    return source.quality
  }
  
  const filename = source.source_id || source.telegram_file_id || ''
  const qualityMatch = filename.match(/(\d{3,4}p)/i)
  return qualityMatch ? qualityMatch[1] : null
}

const getFileName = (source: WatchHistory): string | null => {
  const filename = source.source_id || source.telegram_file_id || ''
  
  if (filename.length > 50) {
    return filename.substring(0, 47) + '...'
  }
  
  return filename || null
}

const shouldShowFileName = (sources: WatchHistory[]): boolean => {
  const sourcesByType = sources.reduce((acc, source) => {
    if (source.source_type !== 'vidfast' && source.source_type !== 'vidlink') {
      acc[source.source_type] = (acc[source.source_type] || 0) + 1
    }
    return acc
  }, {} as Record<string, number>)
  
  return Object.values(sourcesByType).some(count => count > 1)
}

export const Route = createFileRoute('/watch-history-sources/$tmdbId')({
  component: WatchHistorySourcesPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      title: search.title as string,
      sources: search.sources as string, // JSON string of sources array
      posterPath: search.posterPath as string | undefined,
      backdropPath: search.backdropPath as string | undefined,
    }
  }
})

function WatchHistorySourcesPage() {
  const { tmdbId } = Route.useParams()
  const searchParams = Route.useSearch()
  const navigate = useNavigate()
  const isTV = useIsTVDevice()
  
  const [focusedSourceIndex, setFocusedSourceIndex] = useState(0)
  const sourceRefs = useRef<(HTMLButtonElement | null)[]>([])
  const backButtonRef = useRef<HTMLButtonElement>(null)
  
  // Parse sources from search params
  const [sources, setSources] = useState<WatchHistory[]>([])
  const [movieTitle, setMovieTitle] = useState<string>('')
  
  useEffect(() => {
    if (searchParams.sources) {
      try {
        const parsedSources = JSON.parse(searchParams.sources) as WatchHistory[]
        setSources(parsedSources)
        
        // If only one source, auto-select it and play directly
        if (parsedSources.length === 1) {
          handleSourceSelect(parsedSources[0])
        }
      } catch (error) {
        console.error('Failed to parse sources:', error)
      }
    }
    
    if (searchParams.title) {
      setMovieTitle(searchParams.title)
    }
  }, [searchParams, handleSourceSelect])
  
  // Handle source selection - use useCallback to avoid dependency issues
  const handleSourceSelect = useCallback((source: WatchHistory) => {
    // Navigate based on source type (same logic as resumePlayback)
    switch (source.source_type) {
      case 'vidfast':
        navigate({
          to: '/watch/$tmdbId',
          params: { tmdbId: source.tmdb_id.toString() },
          search: {
            source: 'vidfast',
            title: source.title,
            posterPath: source.poster_path || '',
            backdropPath: source.backdrop_path || '',
            overview: source.overview || '',
            releaseDate: source.release_date || '',
            voteAverage: source.vote_average?.toString() || '',
            resumeFrom: source.playback_position_seconds
          }
        })
        break
      case 'vidlink':
        navigate({
          to: '/watch/$tmdbId',
          params: { tmdbId: source.tmdb_id.toString() },
          search: {
            source: 'vidlink',
            title: source.title,
            posterPath: source.poster_path || '',
            backdropPath: source.backdrop_path || '',
            overview: source.overview || '',
            releaseDate: source.release_date || '',
            voteAverage: source.vote_average?.toString() || '',
            resumeFrom: source.playback_position_seconds
          }
        })
        break
      
      case 'vidsrc':
        navigate({
          to: '/watch/$tmdbId',
          params: { tmdbId: source.tmdb_id.toString() },
          search: {
            source: 'vidsrc',
            title: source.title,
            posterPath: source.poster_path || '',
            backdropPath: source.backdrop_path || '',
            overview: source.overview || '',
            releaseDate: source.release_date || '',
            voteAverage: source.vote_average?.toString() || '',
            resumeFrom: source.playback_position_seconds
          }
        })
        break
      
      case 'filestream':
      case 'gdrive':
        navigate({
          to: '/',
          search: {
            playMovie: source.tmdb_id,
            resumeFrom: source.playback_position_seconds
          } as any
        })
        break
    }
  }, [navigate])
  
  const showFilenames = shouldShowFileName(sources)
  const hasMultipleQualities = !showFilenames && sources.length > 1 && sources.some(s => 
    extractQualityFromFilename(s) !== null
  )

  const handleBack = useCallback(() => {
    navigate({ to: '/' })
  }, [navigate])

  // Auto-focus first source on mount for TV
  useEffect(() => {
    if (isTV) {
      const tryFocus = (attempt = 0) => {
        const maxAttempts = 10
        const firstButton = sourceRefs.current[0]
        if (firstButton) {
          firstButton.focus()
          firstButton.scrollIntoView({ block: 'nearest', behavior: 'auto' })
        } else if (attempt < maxAttempts) {
          setTimeout(() => tryFocus(attempt + 1), 50)
        }
      }
      setTimeout(() => tryFocus(), 100)
    }
  }, [isTV, sources.length])

  // Handle keyboard navigation for TV
  useEffect(() => {
    if (!isTV) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        e.stopPropagation()
        const nextIndex = (focusedSourceIndex + 1) % sources.length
        setFocusedSourceIndex(nextIndex)
        setTimeout(() => {
          sourceRefs.current[nextIndex]?.focus()
        }, 0)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        e.stopPropagation()
        const prevIndex = focusedSourceIndex === 0 ? sources.length - 1 : focusedSourceIndex - 1
        setFocusedSourceIndex(prevIndex)
        setTimeout(() => {
          sourceRefs.current[prevIndex]?.focus()
        }, 0)
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        e.stopPropagation()
        if (focusedSourceIndex >= 0 && focusedSourceIndex < sources.length) {
          handleSourceSelect(sources[focusedSourceIndex])
        }
      } else if (e.key === 'Escape' || e.key === 'Backspace') {
        e.preventDefault()
        e.stopPropagation()
        handleBack()
      }
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [isTV, focusedSourceIndex, sources, handleSourceSelect, handleBack])

  if (sources.length === 0) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">No sources available</p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Back Button */}
      <div className="fixed top-6 left-6 z-50">
        <button
          ref={backButtonRef}
          onClick={handleBack}
          onKeyDown={(e) => {
            if (isTV && (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape' || e.key === 'Backspace')) {
              e.preventDefault()
              handleBack()
            }
          }}
          className={`flex items-center gap-2 px-4 py-2 bg-black/50 hover:bg-black/70 text-white rounded-lg transition-all ${isTV ? 'tv-focusable-item' : ''}`}
          tabIndex={isTV ? 0 : undefined}
          aria-label="Go back"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 sm:px-8 lg:px-16 xl:px-20 py-24">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-white mb-2">
              {movieTitle || 'Select Source'}
            </h1>
            <p className="text-gray-400 text-lg">
              Choose a source to continue watching
            </p>
          </div>

          {/* Sources List */}
          <div className="space-y-4">
            {sources.map((source, index) => {
              const progressPercentage = source.total_duration_seconds > 0
                ? Math.floor((source.playback_position_seconds / source.total_duration_seconds) * 100)
                : 0
              
              const quality = extractQualityFromFilename(source)
              const filename = getFileName(source)

              return (
                <motion.button
                  key={source.id}
                  ref={(el) => {
                    sourceRefs.current[index] = el
                  }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => handleSourceSelect(source)}
                  onFocus={() => {
                    setFocusedSourceIndex(index)
                  }}
                  className={`w-full bg-gray-900/50 hover:bg-gray-800/80 rounded-lg p-5 transition-all duration-200 group border border-gray-800 hover:border-gray-700 ${isTV ? 'tv-focusable-item' : ''}`}
                  tabIndex={isTV ? 0 : undefined}
                >
                  <div className="flex items-center gap-4">
                    {/* Play Icon */}
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white/10 group-hover:bg-red-600 flex items-center justify-center transition-all duration-200">
                      <Play className="w-5 h-5 text-white fill-white" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 text-left">
                      {/* Source Badge and Filename/Quality */}
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={`px-2.5 py-1 rounded text-xs font-semibold ${getSourceBadgeColor(source.source_type)}`}>
                          {getSourceLabel(source.source_type)}
                        </span>
                        
                        {/* Show filename if multiple files from same source, otherwise show quality */}
                        {showFilenames && filename ? (
                          <span className="px-2.5 py-1 rounded text-xs font-medium bg-gray-700 text-gray-300 max-w-md truncate">
                            {filename}
                          </span>
                        ) : quality && hasMultipleQualities ? (
                          <span className="px-2.5 py-1 rounded text-xs font-semibold bg-gray-700 text-white">
                            {quality}
                          </span>
                        ) : null}
                        
                        <span className="text-gray-500 text-xs">
                          • {formatTimestamp(source.playback_position_seconds, source.total_duration_seconds)}
                        </span>
                      </div>

                      {/* Progress Bar */}
                      <div className="relative h-1.5 bg-gray-800 rounded-full overflow-hidden mb-2">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${progressPercentage}%` }}
                          transition={{ delay: index * 0.05 + 0.1, duration: 0.4 }}
                          className="absolute inset-y-0 left-0 bg-red-600 rounded-full"
                        />
                      </div>

                      {/* Progress Text */}
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-400">
                          {progressPercentage}% complete
                        </span>
                        <span className="text-gray-600">
                          Last watched {new Date(source.last_watched_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.button>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
